import pptxgen from 'pptxgenjs';
import { getCapacityUnit, getThroughputUnit, formatDriveCapacity } from './configUtilities';
import vduraLogoPng from '../assets/vdura-quote-logo.png';
import { calculateRackLayout } from './RealisticRackVisualization';
import { calculateNetworkPorts, getNetworkOption } from './networkPorts';
import { resolveFlashNodeModel } from './flashNodeModel';
import { getVendorCapabilities } from '../config/vendorRegistry';
import { sanitizeFilenamePart } from './exportFilename';

// ============================================================================
// HELPERS
// ============================================================================

const fmt = (val, decimals = 0) => {
  if (val === undefined || val === null || isNaN(val)) return '0';
  return val.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

// Convert a PNG asset (imported via webpack) to a base64 data URL
const assetToBase64 = (src) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataUrl = canvas.toDataURL('image/png');
      resolve(dataUrl.replace('data:', ''));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });
};

// Infer the MIME type from the asset's extension — SMC's rack images are
// perspective-corrected JPEG product photos, not SVGs like AIC/DELL's.
const mimeFor = (path) =>
  /\.jpe?g$/i.test(path) ? 'image/jpeg' : /\.png$/i.test(path) ? 'image/png' : 'image/svg+xml';

const fetchSvgAsBase64 = (path) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', path, true);
    xhr.responseType = 'arraybuffer';
    xhr.onload = () => {
      if (xhr.status === 200) {
        const bytes = new Uint8Array(xhr.response);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        resolve(`data:${mimeFor(path)};base64,${btoa(binary)}`);
      } else {
        reject(new Error(`Failed to fetch ${path}: ${xhr.status}`));
      }
    };
    xhr.onerror = () => reject(new Error(`Network error fetching ${path}`));
    xhr.send();
  });
};

const getSvgPath = (type, vendor) => {
  if (vendor === 'DELL') {
    switch (type) {
      case 'dNode': return '/images/dell-dnode.svg';
      case 'fNode': return '/images/dell-fnode.svg';
      case 'sNode': return '/images/dell-snode.svg';
      case 'jbod':  return '/images/dell-4Ujbod.svg';
      default:      return null;
    }
  }
  if (vendor === 'SMC') {
    switch (type) {
      case 'dNode':
      case 'fNode': return '/images/1u-front.jpg';
      case 'sNode': return '/images/2u-front.jpg';
      case 'jbod':  return '/images/jbod-front.jpg';   // covers 4U60 and 4U90
      default:      return null;
    }
  }
  switch (type) {
    case 'dNode': return '/images/dnode.svg';
    case 'fNode': return '/images/fnode.svg';
    case 'sNode': return '/images/snode.svg';
    case 'jbod':  return '/images/jbod.svg';
    default:      return null;
  }
};

const svgToBase64Png = (svgBase64, width, height) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/png').split(',')[1]);
    };
    img.src = svgBase64;
  });
};

const prefetchSvgs = async (rackLayout, vendor) => {
  const types = [...new Set(rackLayout.flat().map(u => u.type).filter(t => t !== 'empty' && t !== 'jbod-8u'))];
  const map = {};
  await Promise.all(types.map(async (type) => {
    const path = getSvgPath(type, vendor);
    if (!path) return;
    const svgB64 = await fetchSvgAsBase64(path);
    map[type] = await svgToBase64Png(svgB64, 600, 100);
  }));
  return map;
};

// ============================================================================
// RACK BUILDER
// ============================================================================

const SVG_W      = 500;
const SVG_H      = 750;
const SVG_RAIL_W = 16;

const generateRackSvg = (rack, svgMap, effectiveRackSize, showRack) => {
  const unitH = SVG_H / effectiveRackSize;
  const compW = SVG_W - SVG_RAIL_W * 2;
  const FALLBACK_COLORS = {
    dNode: '#1D6FE8',
    fNode: '#238636',
    sNode: '#D67E00',
    jbod:  '#484F58',
  };

  let els = '';

  if (showRack) {
    els += `<rect x="0" y="0" width="${SVG_W}" height="${SVG_H}" fill="#0a0a0f" stroke="#444444" stroke-width="1"/>`;
    els += `<rect x="0" y="0" width="${SVG_RAIL_W}" height="${SVG_H}" fill="#333333"/>`;
    els += `<rect x="${SVG_W - SVG_RAIL_W}" y="0" width="${SVG_RAIL_W}" height="${SVG_H}" fill="#333333"/>`;
  }

  rack.forEach((unit, slotIndex) => {
    if (unit.type === 'empty' || unit.type === 'jbod-8u') return;
    const isJbod = unit.type === 'jbod';
    if (isJbod && !unit.isJbodOnly) return;

    const unitCount = unit.heightU || (isJbod ? 4 : 1);
    const x = SVG_RAIL_W;
    const y = slotIndex * unitH;
    const h = unitCount * unitH;
    const svgData = svgMap[unit.type];

    if (svgData) {
      els += `<image href="data:image/png;base64,${svgData}" x="${x}" y="${y}" width="${compW}" height="${h}" preserveAspectRatio="none"/>`;
    } else {
      const color = FALLBACK_COLORS[unit.type] || '#444444';
      els += `<rect x="${x}" y="${y}" width="${compW}" height="${h}" fill="${color}" stroke="#555555" stroke-width="1"/>`;
    }
  });

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${SVG_W} ${SVG_H}" width="${SVG_W}" height="${SVG_H}">${els}</svg>`;
};

const buildRackOnSlide = async (slide, pres, config, productDb) => {
  const vendor            = config.hardwareVendor || 'AIC';
  const rackSize          = config.rackSize ?? 42;
  const showRack          = rackSize !== 0;
  const effectiveRackSize = showRack ? rackSize : 42;

  const fullLayout = calculateRackLayout(config, productDb, effectiveRackSize);
  const rackLayout = fullLayout.slice(0, 2);
  const numRacks   = rackLayout.length;

  const svgMap = await prefetchSvgs(rackLayout, vendor);

  const PADDING  = 0.15;
  const LABEL_H  = 0.22;
  const RACK_GAP = 0.2;

  // Use the original 5.0" panel width to size rack images so they aren't stretched
  const ORIGINAL_PANEL_W = 5.0;
  const rackW = (ORIGINAL_PANEL_W - PADDING * 2 - RACK_GAP) / 2;
  const rackH = SLIDE_H - PADDING * 2 - LABEL_H;

  const totalUsedW = numRacks * rackW + (numRacks - 1) * RACK_GAP;
  // Centre the racks within the (wider) panel
  const startX = CONFIG_PANEL_X + (CONFIG_PANEL_W - totalUsedW) / 2;

  for (let i = 0; i < rackLayout.length; i++) {
    const rackX = startX + i * (rackW + RACK_GAP);
    const rackY = PADDING + LABEL_H;

    slide.addText(`Rack ${i + 1}`, {
      x: rackX, y: PADDING,
      w: rackW, h: LABEL_H,
      fontFace: FONT, fontSize: 8, bold: true,
      color: C.mutedGray, align: 'center', valign: 'middle',
      margin: 0,
    });

    const svgString = generateRackSvg(rackLayout[i], svgMap, effectiveRackSize, showRack);
    const b64 = btoa(unescape(encodeURIComponent(svgString)));

    slide.addImage({
      data: `data:image/svg+xml;base64,${b64}`,
      x: rackX, y: rackY,
      w: rackW, h: rackH,
    });
  }
};

// ============================================================================
// SLIDE LAYOUT CONSTANTS  (LAYOUT_WIDE = 13.3" x 7.5")
// ============================================================================
const SLIDE_W = 13.3;
const SLIDE_H = 7.5;

// Rack panel occupies the RIGHT portion of the slide
const CONFIG_PANEL_W = 6.56;
const CONFIG_PANEL_X = SLIDE_W - CONFIG_PANEL_W; // 6.74"

// Content area (stat columns) occupies the LEFT portion
const CONTENT_X = 0.3;                                   // left margin
const CONTENT_W = CONFIG_PANEL_X - 0.2 - CONTENT_X;     // gutter 0.2" before right panel

// Colors (no # prefix — pptxgenjs requirement)
const C = {
  bg:            '111827',
  panelBg:       '1F2937',
  rackBg:        '0D1117',
  orange:        'F97316',
  white:         'FFFFFF',
  lightGray:     'D1D5DB',
  mutedGray:     '9CA3AF',
  divider:       '374151',
  afaBlue:       '3B82F6',
  hybridGreen:   '10B981',
  physicalTeal:  '2D419A',
  sysYellow:     'FBBF24',
  hardwareCyan:  '06B6D4',
};

// Typography
const FONT = 'Calibri';

// Row sizing for 12pt text
const ROW_LABEL_H      = 0.24;
const ROW_VALUE_H      = 0.27;
const ROW_VALUE_H_2LINE = 0.42; // value + small subtext line
const ROW_H            = ROW_LABEL_H + ROW_VALUE_H; // 0.51" per row

// ============================================================================
// HARDWARE SUMMARY
// ============================================================================

export const buildD1NHardwareSummaryRows = (config, productDb) => {
  const ssdPerVelo = productDb?.ssdPerVelo ?? 4;
  const rows = [];
  if (config.veloCount > 0) {
    rows.push({ label: 'Director Nodes (D1N)', value: String(config.veloCount) });
    if (config.veloSsdCapacity > 0) {
      const capStr = formatDriveCapacity(config.veloSsdCapacity);
      rows.push({ label: `NVMe SSDs (${capStr} TB)`, value: String(config.veloCount * ssdPerVelo) });
    }
  }
  return rows;
};

export const buildAFAHardwareSummaryRows = (config, productDb) => {
  const ssdPerVpod = productDb?.ssdPerVpod ?? 12;
  const rows = [];
  if (config.afaEnabled && config.afaNodeCount > 0) {
    // CONF-210: 'Flash Nodes (F1N)' or 'Dual CPU Flash Nodes (F1N)', from the
    // same table the BOM books the SKU from.
    rows.push({ label: resolveFlashNodeModel(config).hardwareRowLabel, value: String(config.afaNodeCount) });
    if (config.afaVpodSsdCapacity > 0) {
      const capStr = formatDriveCapacity(config.afaVpodSsdCapacity);
      rows.push({ label: `NVMe SSDs (${capStr} TB)`, value: String(config.afaNodeCount * ssdPerVpod) });
    }
  }
  return rows;
};

export const buildHybridHardwareSummaryRows = (config, productDb) => {
  const ssdPerVpod = productDb?.ssdPerVpod ?? 12;
  const rows = [];
  if (config.hybridEnabled && config.hybridNodeCount > 0) {
    rows.push({ label: 'Mixed Fleet Nodes (S1N)', value: String(config.hybridNodeCount) });
    if (config.hybridVpodSsdCapacity > 0) {
      const capStr = formatDriveCapacity(config.hybridVpodSsdCapacity);
      rows.push({
        label: `NVMe SSDs (${capStr} TB)`,
        value: (config.hybridNodeCount * ssdPerVpod).toLocaleString('en-US'),
      });
    }
    const jbodLabel = config.hybridHddCapacity > 0
      ? `4U JBODs (J${config.jbodSize}-${config.jbodSize * config.hybridHddCapacity})`
      : `4U JBODs (J${config.jbodSize})`;
    rows.push({ label: jbodLabel, value: String(config.hybridNodeCount) });
    if (config.hybridHddCapacity > 0) {
      rows.push({
        label: `${config.hybridHddCapacity} TB HDDs`,
        value: (config.hybridNodeCount * config.jbodSize).toLocaleString('en-US'),
      });
    }
  }
  return rows;
};

// CONF-93: the rack slide previously had no representation of the network
// at all. This mirrors the buildXHardwareSummaryRows shape above but is
// keyed off `capabilities` (the vendor's network option registry) rather
// than `productDb`, since network options live in vendorRegistry.js.
export const buildNetworkHardwareSummaryRows = (config, capabilities) => {
  const networkOption = getNetworkOption(capabilities, config.interfaceOption);
  const { totalPorts } = calculateNetworkPorts(config, capabilities);
  return [
    { label: 'Network', value: networkOption?.label ?? config.interfaceOption },
    { label: 'Protocol', value: config.networkProtocol || 'TCP' },
    { label: 'Network Ports', value: String(totalPorts) },
  ];
};

// ============================================================================
// MAIN EXPORT
// ============================================================================

/**
 * Generates a single-slide PPTX summarizing the VDURA V5000 system configuration.
 *
 * @param {Object} metrics   - Calculated system metrics object
 * @param {Object} config    - Current configurator config object
 * @param {Object} productDb - Product database for the current vendor
 */
export const generateRackSlidePPTX = async (metrics, config, productDb) => {
  const pres = new pptxgen();
  pres.layout = 'LAYOUT_WIDE'; // 13.3" x 7.5"
  pres.title  = 'VDURA V5000 System Configuration';
  pres.author = 'VDURA Configurator';

  const slide = pres.addSlide();

  // ── Background ────────────────────────────────────────────────────────────
  slide.background = { color: C.bg };

  // ── Right panel: rack background ─────────────────────────────────────────
  slide.addShape(pres.shapes.RECTANGLE, {
    x: CONFIG_PANEL_X, y: 0, w: CONFIG_PANEL_W, h: SLIDE_H,
    fill: { color: C.rackBg },
    line: { color: C.divider, width: 1 },
  });

  // ── Right panel: rack visualization ──────────────────────────────────────
  try {
    await buildRackOnSlide(slide, pres, config, productDb);
  } catch (e) {
    console.error('buildRackOnSlide failed:', e);
    slide.addText('RACK\nVISUALIZATION', {
      x: CONFIG_PANEL_X, y: SLIDE_H / 2 - 0.4,
      w: CONFIG_PANEL_W, h: 0.8,
      align: 'center', valign: 'middle',
      fontFace: FONT, fontSize: 11,
      color: C.divider, bold: true, charSpacing: 4,
    });
  }

  // ── Orange accent strip (top of left content area) ────────────────────────
  slide.addShape(pres.shapes.RECTANGLE, {
    x: 0, y: 0, w: CONFIG_PANEL_X, h: 0.06,
    fill: { color: C.orange },
    line: { color: C.orange, width: 0 },
  });

  // ── Logo ──────────────────────────────────────────────────────────────────
  try {
    const logoData = await assetToBase64(vduraLogoPng);
    if (logoData) {
      slide.addImage({
        data: logoData,
        x: CONTENT_X, y: 0.15, w: 2.2, h: 0.38,
      });
    }
  } catch (e) {
    slide.addText('VDURA', {
      x: CONTENT_X, y: 0.15, w: 2.2, h: 0.38,
      fontFace: FONT, fontSize: 20, bold: true, color: C.orange,
    });
  }

  // ── Slide title ───────────────────────────────────────────────────────────
  const titleLabel = config.quoteDescription
    ? `V5000 System Configuration  —  ${config.quoteDescription}`
    : 'V5000 System Configuration';

  slide.addText(titleLabel, {
    x: CONTENT_X + 2.35, y: 0.15, w: CONTENT_W - 2.35, h: 0.38,
    fontFace: FONT, fontSize: 14, bold: true,
    color: C.lightGray, align: 'right', valign: 'middle',
    margin: 0,
  });

  // ── Thin divider under header ─────────────────────────────────────────────
  slide.addShape(pres.shapes.RECTANGLE, {
    x: CONTENT_X, y: 0.60, w: CONTENT_W, h: 0.01,
    fill: { color: C.divider },
    line: { color: C.divider, width: 0 },
  });

  // ── SECTION BUILDER HELPERS ───────────────────────────────────────────────

  const SECTION_TOP = 0.70;
  const SECTION_H   = SLIDE_H - SECTION_TOP - 0.25;
  const GAP         = 0.12;

  const hasAFA    = !!config.afaEnabled;
  const hasHybrid = !!config.hybridEnabled;

  // Always size for 3 columns; disabled columns just leave empty space
  const colW = (CONTENT_W - GAP * 2) / 3;

  const drawCard = (colIndex, accentColor, title) => {
    const x = CONTENT_X + colIndex * (colW + GAP);
    const y = SECTION_TOP;

    slide.addShape(pres.shapes.RECTANGLE, {
      x, y, w: colW, h: SECTION_H,
      fill: { color: C.panelBg },
      line: { color: C.divider, width: 1 },
    });
    slide.addShape(pres.shapes.RECTANGLE, {
      x, y, w: 0.055, h: SECTION_H,
      fill: { color: accentColor },
      line: { color: accentColor, width: 0 },
    });
    slide.addText(title, {
      x: x + 0.12, y: y + 0.08,
      w: colW - 0.18, h: 0.28,
      fontFace: FONT, fontSize: 12, bold: true,
      color: accentColor, margin: 0,
    });
    slide.addShape(pres.shapes.RECTANGLE, {
      x: x + 0.12, y: y + 0.40,
      w: colW - 0.24, h: 0.008,
      fill: { color: accentColor },
      line: { color: accentColor, width: 0 },
    });

    return { cardX: x, rowStartY: y + 0.46 };
  };

  const addRow = (cardX, rowY, label, value, valueColor = C.white, subtext = null) => {
    const valueH = subtext ? ROW_VALUE_H_2LINE : ROW_VALUE_H;
    slide.addText(label, {
      x: cardX + 0.12, y: rowY,
      w: colW - 0.18, h: ROW_LABEL_H,
      fontFace: FONT, fontSize: 12, color: C.mutedGray,
      margin: 0, valign: 'middle',
    });
    if (subtext) {
      slide.addText([
        { text: value,   options: { bold: true,  color: valueColor,  fontSize: 12, breakLine: true } },
        { text: subtext, options: { bold: false, color: C.mutedGray, fontSize: 9 } },
      ], {
        x: cardX + 0.12, y: rowY + ROW_LABEL_H,
        w: colW - 0.18, h: valueH,
        fontFace: FONT, margin: 0, valign: 'top',
      });
    } else {
      slide.addText(value, {
        x: cardX + 0.12, y: rowY + ROW_LABEL_H,
        w: colW - 0.18, h: valueH,
        fontFace: FONT, fontSize: 12, bold: true,
        color: valueColor, margin: 0, valign: 'middle',
      });
    }
    return rowY + ROW_LABEL_H + valueH;
  };

  const HWROW_H = 0.26;
  const addHwRow = (cardX, rowY, label, value) => {
    const labelW = label.length * 0.058 + 0.12;
    slide.addText(label, {
      x: cardX + 0.12, y: rowY,
      w: labelW, h: HWROW_H,
      fontFace: FONT, fontSize: 10, color: C.mutedGray,
      margin: 0, valign: 'middle', wrap: false,
    });
    slide.addText(value, {
      x: cardX + 0.12 + labelW + 0.06, y: rowY,
      w: colW - 0.30 - labelW, h: HWROW_H,
      fontFace: FONT, fontSize: 12, bold: true, color: C.white,
      align: 'right', margin: 0, valign: 'middle',
    });
    return rowY + HWROW_H;
  };

  const capUnit  = getCapacityUnit(config.isPBUnit);
  const capUnitR = getCapacityUnit(config.isPBUnit, 'r');
  const capUnitE = getCapacityUnit(config.isPBUnit, 'e');
  const thruUnit = getThroughputUnit(config.isPBUnit);
  const isBase   = config.systemType === 'BaseSystem';

  // ── COLUMN 0: System Attributes ───────────────────────────────────────────
  {
    const { cardX, rowStartY } = drawCard(0, C.sysYellow, 'System Attributes');
    let ry = rowStartY;
    ry = addRow(cardX, ry, `Raw Capacity (${capUnitR})`,
      `${fmt(metrics.totalRawCapacity)} ${capUnitR}`);
    ry = addRow(cardX, ry, `Useable Capacity (${capUnit})`,
      `${fmt(metrics.totalEffectiveCapacity)} ${capUnit}`);
    ry = addRow(cardX, ry, `Effective Capacity (${capUnitE})`,
      `${fmt(metrics.totalCompressedEffectiveCapacity)} ${capUnitE}`);
    ry = addRow(cardX, ry, 'Shipment Quarter',
      config.quarter || '—');

    slide.addShape(pres.shapes.RECTANGLE, {
      x: cardX + 0.12, y: ry + 0.05,
      w: colW - 0.24, h: 0.008,
      fill: { color: C.divider },
      line: { color: C.divider, width: 0 },
    });
    ry += 0.20;

    slide.addText('Physical Attributes', {
      x: cardX + 0.12, y: ry,
      w: colW - 0.18, h: 0.26,
      fontFace: FONT, fontSize: 12, bold: true,
      color: C.hardwareCyan, margin: 0,
    });
    ry += 0.30;
    ry = addRow(cardX, ry, 'Rack Units / Racks',
      `${fmt(metrics.totalRU)} RU / ${Math.ceil(metrics.totalRU / 42)}`);
    const maxPwrDisplay = metrics.totalMaxPower > 0
      ? `${fmt(metrics.totalMaxPower / 1000, 1)} kW`
      : 'N/A';
    ry = addRow(cardX, ry, 'Power (Typical / Max)',
      `${fmt(metrics.totalNominalPower / 1000, 1)} kW / ${maxPwrDisplay}`);
    ry = addRow(cardX, ry, 'Weight',
      `${fmt(metrics.totalWeight)} kg`);

    // CONF-93: the network rows are deliberately unconditional — this card is
    // the whole-system Physical Attributes card (rack units, power, weight),
    // not a director-specific one, and network option/protocol/port count are
    // whole-system facts too. That means the "Hardware Summary" header below
    // (gated on d1nRows.length > 0) now always renders even on an Expansion
    // quote, where applySystemTypeTransition (domain/rules/transitions.ts)
    // zeroes veloCount for the config's whole life and buildD1NHardwareSummaryRows
    // alone would return []. That's intentional, not a bug to "fix" back: an
    // Expansion quote's port total (storage-node NICs) is still real and worth
    // showing, and gating it out would leave Expansion slides with no network
    // information at all.
    const capabilities = getVendorCapabilities(config.hardwareVendor);
    const d1nRows = [
      ...buildD1NHardwareSummaryRows(config, productDb),
      ...buildNetworkHardwareSummaryRows(config, capabilities),
    ];
    if (d1nRows.length > 0) {
      slide.addShape(pres.shapes.RECTANGLE, {
        x: cardX + 0.12, y: ry + 0.08,
        w: colW - 0.24, h: 0.008,
        fill: { color: C.divider },
        line: { color: C.divider, width: 0 },
      });
      ry += 0.22;
      slide.addText('Hardware Summary', {
        x: cardX + 0.12, y: ry,
        w: colW - 0.18, h: 0.26,
        fontFace: FONT, fontSize: 12, bold: true,
        color: C.hardwareCyan, margin: 0,
      });
      ry += 0.30;
      for (const { label, value } of d1nRows) {
        ry = addHwRow(cardX, ry, label, value);
      }
    }
  }

  // ── COLUMN 1 (optional): AFA Storage Set ──────────────────────────────────
  if (hasAFA) {
    const { cardX, rowStartY } = drawCard(1, C.afaBlue, 'AFA Storage Set');
    let ry = rowStartY;
    ry = addRow(cardX, ry, `Raw (${capUnitR})`,
      `${fmt(metrics.totalAfaRawCapacity)} ${capUnitR}`);
    ry = addRow(cardX, ry, `Useable (${capUnit})`,
      `${fmt(metrics.afaEffectiveCapacity)} ${capUnit}`);
    ry = addRow(cardX, ry, `Effective (${capUnitE})`,
      `${fmt(metrics.afaCompressedEffectiveCapacity)} ${capUnitE}`,
      C.white,
      `(@${config.afaCompressionRatio}:1 reduction)`);
    if (isBase) {
      // CONF-125: IOPs hidden — values stale, will restore when updated data is available
      // ry = addRow(cardX, ry, 'IOPS', `Up to ${fmt(metrics.afaIops, 1)} M/s`);
      ry = addRow(cardX, ry, `Sustained R/W (${thruUnit})`,
        `Up to ${fmt(metrics.afaReadTransferRate)} / ${fmt(metrics.afaWriteTransferRate)}`);
      ry = addHwRow(cardX, ry, 'VPOD Spares',
        `${fmt(config.afaSpares)}`);
      ry = addHwRow(cardX, ry, 'Encryption',
        config.afaEndToEndEncryption ? 'Enabled' : 'Disabled');
    }
    const afaHwRows = buildAFAHardwareSummaryRows(config, productDb);
    if (afaHwRows.length > 0) {
      slide.addShape(pres.shapes.RECTANGLE, {
        x: cardX + 0.12, y: ry + 0.08,
        w: colW - 0.24, h: 0.008,
        fill: { color: C.divider },
        line: { color: C.divider, width: 0 },
      });
      ry += 0.22;
      slide.addText('Hardware Summary', {
        x: cardX + 0.12, y: ry,
        w: colW - 0.18, h: 0.26,
        fontFace: FONT, fontSize: 12, bold: true,
        color: C.hardwareCyan, margin: 0,
      });
      ry += 0.30;
      for (const { label, value } of afaHwRows) {
        ry = addHwRow(cardX, ry, label, value);
      }
    }
  }

  // ── COLUMN 1 or 2 (optional): Hybrid Storage Set ─────────────────────────
  // Sits in col 1 when AFA is disabled, col 2 when AFA occupies col 1
  if (hasHybrid) {
    const { cardX, rowStartY } = drawCard(hasAFA ? 2 : 1, C.hybridGreen, 'Mixed Fleet Storage Set');
    let ry = rowStartY;
    ry = addRow(cardX, ry, `Raw (${capUnitR})`,
      `${fmt(metrics.totalHybridRawCapacity)} ${capUnitR}`,
      C.white,
      `(${fmt(metrics.totalHybridSsdCapacity)} ${capUnit} NVMe)`);
    ry = addRow(cardX, ry, `Useable (${capUnit})`,
      `${fmt(metrics.hybridEffectiveCapacity)} ${capUnit}`);
    ry = addRow(cardX, ry, `Effective (${capUnitE})`,
      `${fmt(metrics.hybridCompressedEffectiveCapacity)} ${capUnitE}`,
      C.white,
      `(@${config.hybridCompressionRatio}:1 reduction)`);
    if (isBase) {
      // CONF-125: IOPs hidden — values stale, will restore when updated data is available
      // ry = addRow(cardX, ry, 'IOPS', `Up to ${fmt(metrics.hybridIops, 1)} M/s`);
      ry = addRow(cardX, ry, `Burst R/W (${thruUnit})`,
        `Up to ${fmt(metrics.hybridBurstReadTransferRate)} / ${fmt(metrics.hybridBurstWriteTransferRate)}`);
      ry = addRow(cardX, ry, `Sustained R/W (${thruUnit})`,
        `Up to ${fmt(metrics.hybridReadTransferRate)} / ${fmt(metrics.hybridWriteTransferRate)}`);
      ry = addHwRow(cardX, ry, 'VPOD Spares',
        `${fmt(config.hybridSpares)}`);
      ry = addHwRow(cardX, ry, 'Encryption',
        config.hybridEndToEndEncryption ? 'Enabled' : 'Disabled');
      ry = addHwRow(cardX, ry, 'MLEC',
        config.hybridMLEC ? 'Enabled' : 'Disabled');
    }
    const hybridHwRows = buildHybridHardwareSummaryRows(config, productDb);
    if (hybridHwRows.length > 0) {
      slide.addShape(pres.shapes.RECTANGLE, {
        x: cardX + 0.12, y: ry + 0.08,
        w: colW - 0.24, h: 0.008,
        fill: { color: C.divider },
        line: { color: C.divider, width: 0 },
      });
      ry += 0.22;
      slide.addText('Hardware Summary', {
        x: cardX + 0.12, y: ry,
        w: colW - 0.18, h: 0.26,
        fontFace: FONT, fontSize: 12, bold: true,
        color: C.hardwareCyan, margin: 0,
      });
      ry += 0.30;
      for (const { label, value } of hybridHwRows) {
        ry = addHwRow(cardX, ry, label, value);
      }
    }
  }

  // ── Footer ────────────────────────────────────────────────────────────────
  slide.addText('VDURA Confidential & Proprietary — NDA Required', {
    x: CONTENT_X, y: SLIDE_H - 0.22,
    w: CONTENT_W, h: 0.18,
    fontFace: FONT, fontSize: 7, color: C.mutedGray,
    align: 'right', margin: 0,
  });

  // ── Save ──────────────────────────────────────────────────────────────────
  const filename = `VDURA_V5000_System_Slide_${sanitizeFilenamePart(config.quarter, 'Draft')}.pptx`;
  await pres.writeFile({ fileName: filename });
};
